import {
  Alert,
  Button,
  Checkbox,
  Checklist,
  Drawer,
  Field,
  FieldGroup,
  Input,
  Loader,
  Select
} from '@itds/elements/index.js';
import { STATES } from '@itds/Constants';
import React, { useEffect, useState } from 'react';

import Api from '@carrier/utils/Api';
import PropTypes from 'prop-types';

const Edit = ({ rider, riderId, onClose, onSave, show }) => {
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    beacon_product_id: null,
    beacon_id: null,
    product_type_detail: 'Fixed Indexed Annuity',
    name: '',
    rider_type: 'DefaultGMWB',
    states: '',
    variant: '',
    // New Rider Details
    rider_name: '',
    min_issue_age: '',
    max_issue_age: '',
    min_age_start_income: '',
    waiting_period_min_income_year: '',
    benefit_description: '',
    cancellation_terms: '',
    // Banding
    single_percent: '',
    joint_percent: '',
    single_age_band: '',
    joint_age_band: '',
    gender: 'Both',
    has_banding_bonus: false,
    banding_bonus_years: '',
    banding_bonus_percent: '',
    // Rollup
    is_rollup_age_dependent: false,
    rollup_age_tiers: [{ age: '', rollup_percent: '' }],
    is_rollup_policy_year_dependent: false,
    rollup_policy_years: [{ year: '', rollup_percent: '' }],
    static_rollup_percent: '',
    // GMB Charge
    charge_bps: '',
    charge_basis_type: 'benefit_base',
    charge_frequency: 'annual',
    charge_term: '',
    // Legacy fields for compatibility
    use_quote_column_states: true,
    band_ids: [],
    product_ids: [],
    health_category_ids: [],
    modal_factor_id: ''
  });
  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bands, setBands] = useState([]);
  const [products, setProducts] = useState([]);
  const [health_categories, setHealthCategories] = useState([]);
  const [modal_factors, setModalFactors] = useState([]);

  useEffect(() => {
    const new_rider = rider || {};
    const states = {};
    const age_rates = {};

    STATES.forEach(state => {
      const name = `state_${state.value}`;

      states[name] = new_rider[name];
    });

    [...Array(100)].forEach((_v, i) => {
      const name = `age_${i + 1}`;

      age_rates[name] = new_rider[name];
    });
    setData({
      id: new_rider.id,
      carrier_id: new_rider.carrier_id,
      data_version_id: new_rider.id,
      beacon_product_id: new_rider.beacon_id || null,
      beacon_id: new_rider.beacon_id || null,
      product_type_detail: new_rider.product_type_detail || 'Fixed Indexed Annuity',
      name: new_rider.name || '',
      rider_type: new_rider.rider_type || 'DefaultGMWB',
      states: new_rider.states || '',
      variant: new_rider.variant || '',
      // New Rider Details
      rider_name: new_rider.rider_name || '',
      min_issue_age: new_rider.min_issue_age || '',
      max_issue_age: new_rider.max_issue_age || '',
      min_age_start_income: new_rider.min_age_start_income || '',
      waiting_period_min_income_year: new_rider.waiting_period_min_income_year || '',
      benefit_description: new_rider.benefit_description || '',
      cancellation_terms: new_rider.cancellation_terms || '',
      // Banding
      single_percent: new_rider.single_percent || '',
      joint_percent: new_rider.joint_percent || '',
      single_age_band: new_rider.single_age_band || '',
      joint_age_band: new_rider.joint_age_band || '',
      gender: new_rider.gender || 'Both',
      has_banding_bonus: new_rider.has_banding_bonus || false,
      banding_bonus_years: new_rider.banding_bonus_years || '',
      banding_bonus_percent: new_rider.banding_bonus_percent || '',
      // Rollup
      is_rollup_age_dependent: new_rider.is_rollup_age_dependent || false,
      rollup_age_tiers: new_rider.rollup_age_tiers || [{ age: '', rollup_percent: '' }],
      is_rollup_policy_year_dependent: new_rider.is_rollup_policy_year_dependent || false,
      rollup_policy_years: new_rider.rollup_policy_years || [{ year: '', rollup_percent: '' }],
      static_rollup_percent: new_rider.static_rollup_percent || '',
      // GMB Charge
      charge_bps: new_rider.charge_bps || '',
      charge_basis_type: new_rider.charge_basis_type || 'benefit_base',
      charge_frequency: new_rider.charge_frequency || 'annual',
      charge_term: new_rider.charge_term || '',
      // Legacy fields for compatibility
      use_quote_column_states: new_rider.use_quote_column_states || true,
      band_ids: new_rider.band_ids || [],
      product_ids: new_rider.product_ids || [],
      health_category_ids: new_rider.health_category_ids || [],
      modal_factor_id: new_rider.modal_factor_id || '',
      ...states,
      ...age_rates
    });

    setErrors([]);
    const carrier_id = new_rider.carrier_id;
    const data_version_id = new_rider.id;

    if (carrier_id && data_version_id) {
      const params = {
        carrier_id,
        data_version_id
      };

      setLoading(true);

      Promise.all([
        Api.loadBands(params),
        Api.loadProducts(params),
        Api.loadHealthCategories(params),
        Api.loadModalFactors(params)
      ])
        .then(values => {
          setBands(values[0].data);
          setProducts(values[1].data);
          setHealthCategories(values[2].data);
          setModalFactors(values[3].data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading dropdown data:', error);
          setLoading(false);
        });
    }

    return () => {};
  }, [rider]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheckboxChange = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleStatesChange = selected_states => {
    const updated_states = {};

    STATES.forEach(state => {
      updated_states[`state_${state.value}`] = selected_states.includes(state.value);
    });

    setData({ ...data, ...updated_states });
  };

  // Helper functions for managing arrays
  const handleAddRollupAgeTier = () => {
    setData({
      ...data,
      rollup_age_tiers: [...data.rollup_age_tiers, { age: '', rollup_percent: '' }]
    });
  };
  const handleRemoveRollupAgeTier = index => {
    const newTiers = data.rollup_age_tiers.filter((_, i) => i !== index);

    setData({ ...data, rollup_age_tiers: newTiers });
  };
  const handleRollupAgeTierChange = (index, field, value) => {
    const newTiers = [...data.rollup_age_tiers];

    newTiers[index][field] = value;
    setData({ ...data, rollup_age_tiers: newTiers });
  };

  const handleAddPolicyYear = () => {
    setData({
      ...data,
      rollup_policy_years: [...data.rollup_policy_years, { year: '', rollup_percent: '' }]
    });
  };
  const handleRemovePolicyYear = index => {
    const newYears = data.rollup_policy_years.filter((_, i) => i !== index);

    setData({ ...data, rollup_policy_years: newYears });
  };
  const handlePolicyYearChange = (index, field, value) => {
    const newYears = [...data.rollup_policy_years];

    newYears[index][field] = value;
    setData({ ...data, rollup_policy_years: newYears });
  };
  const handleSave = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    setErrors(errors);

    if (!errors.length) {
      const payload = {
        beacon_product_id: data.beacon_id ? parseInt(String(data.beacon_id), 10) : null,
        beacon_id: 0,
        version_id: data.id ? parseInt(data.id, 10) : null,
        product_type_detail: data.product_type_detail || 'Fixed Indexed Annuity',
        rider_name: data.rider_name || data.name,
        rider_type: data.rider_type || 'DefaultGMWB',
        states_available: data.states || '',
        min_issue_age: data.min_issue_age ? parseInt(data.min_issue_age, 10) : null,
        max_issue_age: data.max_issue_age ? parseInt(data.max_issue_age, 10) : null,
        min_age_to_start_income: data.min_age_start_income
          ? parseInt(data.min_age_start_income, 10)
          : null,
        waiting_period: data.waiting_period_min_income_year
          ? parseInt(data.waiting_period_min_income_year, 10)
          : null,
        benefit_description: data.benefit_description || '',
        canceallation_terms: data.cancellation_terms || '',
        gender: data.gender === 'Both' ? 'Unisex' : data.gender,
        banding_bonus_years: data.banding_bonus_years
          ? parseInt(data.banding_bonus_years, 10)
          : null,
        banding_bonus_percent: data.banding_bonus_percent
          ? parseFloat(data.banding_bonus_percent)
          : null,
        is_rollup_age_dependent: Boolean(data.is_rollup_age_dependent),
        is_rollup_policy_year_dependent: Boolean(data.is_rollup_policy_year_dependent),

        // Transform age rate bands from banding section
        age_rate_bands: [
          {
            single_rate: data.single_percent ? parseFloat(data.single_percent) : null,
            joint_rate: data.joint_percent ? parseFloat(data.joint_percent) : null,
            single_age_band: data.single_age_band ? parseInt(data.single_age_band, 10) : null,
            joint_age_band: data.joint_age_band ? parseInt(data.joint_age_band, 10) : null
          }
        ].filter(band => band.single_rate !== null || band.joint_rate !== null),

        // Transform rollups from age tiers and policy years
        roll_ups: [
          // Age-based rollups
          ...data.rollup_age_tiers
            .filter(tier => tier.age && tier.rollup_percent) // Filter out empty entries
            .map(tier => ({
              age: parseInt(tier.age, 10),
              age_percent: parseFloat(tier.rollup_percent),
              year: null,
              year_percent: null
            })),

          // Policy year-based rollups
          ...data.rollup_policy_years
            .filter(year => year.year && year.rollup_percent) // Filter out empty entries
            .map(year => ({
              age: null,
              age_percent: null,
              year: parseInt(year.year, 10),
              year_percent: parseFloat(year.rollup_percent)
            })),

          // Static rollup if no age or policy year dependency
          ...(!data.is_rollup_age_dependent &&
          !data.is_rollup_policy_year_dependent &&
          data.static_rollup_percent
            ? [
                {
                  age: null,
                  age_percent: null,
                  year: null,
                  year_percent: parseFloat(data.static_rollup_percent)
                }
              ]
            : [])
        ],

        // Transform charges from GMB Charge section
        charges: data.charge_bps
          ? [
              {
                charge_bps: parseInt(data.charge_bps, 10),
                charge_basis_type: (() => {
                  switch (data.charge_basis_type) {
                    case 'benefit_base':
                      return 'Benefit Base';
                    case 'account_value':
                      return 'Account Value';
                    case 'fixed_account_value':
                      return 'Fixed Account Value';
                    case 'accumulation_value':
                      return 'Accumulation Value';
                    default:
                      return 'Benefit Base';
                  }
                })(),
                charge_term: data.charge_term || '',
                charge_frequency: (() => {
                  switch (data.charge_frequency) {
                    case 'annual':
                      return 'Annual';
                    case 'quarterly':
                      return 'Quarterly';
                    case 'monthly':
                      return 'Monthly';
                    default:
                      return 'Annual';
                  }
                })()
              }
            ]
          : []
      };

      Api.saveAnnuityRider(riderId || null, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(error => {
          console.error('Save error:', error);
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button onClick={handleSave} style={{}}>
          {data.id ? 'Save Changes' : 'Create Rider'}
        </Button>
      }
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Rider' : 'New Rider'}
      wide={true}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {save_error ? (
            <Alert type='error'>An error occurred while trying to save the rider.</Alert>
          ) : null}{' '}
          {/* Basic Information */}{' '}
          <Field label='Type' style={{}}>
            <Select
              onChange={handleInputChange.bind(null, 'rider_type')}
              options={[
                {
                  value: 'DefaultGMWB',
                  label: 'GMWB - Guaranteed Minimum Withdrawal Benefit'
                }
              ]}
              style={{}}
              value={data.rider_type}
            />
          </Field>
          <Field label='Name' style={{}}>
            <Input
              invalid={errors.includes('name')}
              onChange={handleInputChange.bind(null, 'name')}
              required={true}
              style={{}}
              value={data.name || ''}
            />
          </Field>
          <Field label='States' style={{}}>
            <Input
              onChange={handleInputChange.bind(null, 'states')}
              style={{}}
              value={data.states || ''}
            />
          </Field>
          <Field description='Used on age tier table' label='Variant' style={{}}>
            <Input
              onChange={handleInputChange.bind(null, 'variant')}
              style={{}}
              value={data.variant || ''}
            />
          </Field>
          {/* New Rider Details */}
          <FieldGroup contained={true} label='Rider Details'>
            <Field label='Rider Name' style={{}}>
              <Input
                onChange={handleInputChange.bind(null, 'rider_name')}
                style={{}}
                value={data.rider_name || ''}
              />
            </Field>

            <div style={{ display: 'flex' }}>
              <Field label='Min Issue Age' style={{ flex: 1, marginRight: 10 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'min_issue_age')}
                  style={{}}
                  type='number'
                  value={data.min_issue_age || ''}
                />
              </Field>
              <Field label='Max Issue Age' style={{ flex: 1 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'max_issue_age')}
                  style={{}}
                  type='number'
                  value={data.max_issue_age || ''}
                />
              </Field>
            </div>

            <Field label='Min Age to Start Income' style={{}}>
              <Input
                onChange={handleInputChange.bind(null, 'min_age_start_income')}
                style={{}}
                type='number'
                value={data.min_age_start_income || ''}
              />
            </Field>

            <Field label='Waiting Period / Minimum Income Start Year' style={{}}>
              <Input
                onChange={handleInputChange.bind(null, 'waiting_period_min_income_year')}
                style={{}}
                type='number'
                value={data.waiting_period_min_income_year || ''}
              />
            </Field>

            <Field label='Benefit Description' style={{}}>
              <Input
                height={100}
                onChange={handleInputChange.bind(null, 'benefit_description')}
                style={{}}
                type='textarea'
                value={data.benefit_description || ''}
              />
            </Field>

            <Field label='Cancellation Terms' style={{}}>
              <Input
                height={100}
                onChange={handleInputChange.bind(null, 'cancellation_terms')}
                style={{}}
                type='textarea'
                value={data.cancellation_terms || ''}
              />
            </Field>
          </FieldGroup>
          {/* Banding */}
          <FieldGroup contained={true} label='Banding'>
            <div style={{ display: 'flex' }}>
              <Field label='Single Percent' style={{ flex: 1, marginRight: 10 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'single_percent')}
                  style={{}}
                  type='number'
                  value={data.single_percent || ''}
                />
              </Field>
              <Field label='Joint Percent' style={{ flex: 1 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'joint_percent')}
                  style={{}}
                  type='number'
                  value={data.joint_percent || ''}
                />
              </Field>
            </div>
            <div style={{ display: 'flex' }}>
              <Field label='Single Age Band' style={{ flex: 1, marginRight: 10 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'single_age_band')}
                  style={{}}
                  type='number'
                  value={data.single_age_band || ''}
                />
              </Field>
              <Field label='Joint Age Band' style={{ flex: 1 }}>
                <Input
                  onChange={handleInputChange.bind(null, 'joint_age_band')}
                  style={{}}
                  type='number'
                  value={data.joint_age_band || ''}
                />
              </Field>
            </div>{' '}
            <Field label='Gender' style={{}}>
              <Select
                onChange={handleInputChange.bind(null, 'gender')}
                options={[
                  { value: 'Both', label: 'Any Gender' },
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' }
                ]}
                style={{}}
                value={data.gender}
              />
            </Field>
            <Field style={{}}>
              <Checkbox
                checked={data.has_banding_bonus}
                label='Banding Bonus for waiting x policy years?'
                onChange={handleCheckboxChange.bind(null, 'has_banding_bonus')}
              />
            </Field>
            {data.has_banding_bonus && (
              <div style={{ display: 'flex' }}>
                <Field label='Years' style={{ flex: 1, marginRight: 10 }}>
                  <Input
                    onChange={handleInputChange.bind(null, 'banding_bonus_years')}
                    style={{}}
                    type='number'
                    value={data.banding_bonus_years || ''}
                  />
                </Field>
                <Field label='Bonus Percent' style={{ flex: 1 }}>
                  <Input
                    onChange={handleInputChange.bind(null, 'banding_bonus_percent')}
                    style={{}}
                    type='number'
                    value={data.banding_bonus_percent || ''}
                  />
                </Field>
              </div>
            )}
          </FieldGroup>
          {/* Rollup */}
          <FieldGroup contained={true} label='Rollup'>
            <Field style={{}}>
              <Checkbox
                checked={data.is_rollup_age_dependent}
                label='Is the rollup percent issue age dependent?'
                onChange={handleCheckboxChange.bind(null, 'is_rollup_age_dependent')}
              />
            </Field>

            {data.is_rollup_age_dependent && (
              <div>
                <h4>Age Based Rollup Tiers</h4>
                {data.rollup_age_tiers.map((tier, index) => (
                  <div
                    key={index}
                    style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
                  >
                    <Field label='Age' style={{ flex: 1, marginRight: 10 }}>
                      <Input
                        onChange={e => handleRollupAgeTierChange(index, 'age', e.target.value)}
                        style={{}}
                        type='number'
                        value={tier.age}
                      />
                    </Field>
                    <Field label='Rollup %' style={{ flex: 1, marginRight: 10 }}>
                      <Input
                        onChange={e =>
                          handleRollupAgeTierChange(index, 'rollup_percent', e.target.value)
                        }
                        style={{}}
                        type='number'
                        value={tier.rollup_percent}
                      />
                    </Field>
                    <Button
                      color='RED'
                      onClick={() => handleRemoveRollupAgeTier(index)}
                      small={true}
                      style={{}}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button onClick={handleAddRollupAgeTier} small={true} style={{}}>
                  Add Age Tier
                </Button>
              </div>
            )}

            {!data.is_rollup_age_dependent && (
              <>
                <Field style={{}}>
                  <Checkbox
                    checked={data.is_rollup_policy_year_dependent}
                    label='Is rollup policy year dependent?'
                    onChange={handleCheckboxChange.bind(null, 'is_rollup_policy_year_dependent')}
                  />
                </Field>

                {data.is_rollup_policy_year_dependent ? (
                  <div>
                    <h4>Policy Year Based Rollup</h4>
                    {data.rollup_policy_years.map((yearData, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
                      >
                        <Field label='Policy Year' style={{ flex: 1, marginRight: 10 }}>
                          <Input
                            onChange={e => handlePolicyYearChange(index, 'year', e.target.value)}
                            style={{}}
                            type='number'
                            value={yearData.year}
                          />
                        </Field>
                        <Field label='Rollup %' style={{ flex: 1, marginRight: 10 }}>
                          <Input
                            onChange={e =>
                              handlePolicyYearChange(index, 'rollup_percent', e.target.value)
                            }
                            style={{}}
                            type='number'
                            value={yearData.rollup_percent}
                          />
                        </Field>
                        <Button
                          color='RED'
                          onClick={() => handleRemovePolicyYear(index)}
                          small={true}
                          style={{}}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button onClick={handleAddPolicyYear} small={true} style={{}}>
                      Add Policy Year
                    </Button>
                  </div>
                ) : (
                  <Field label='Static Rollup Percent' style={{}}>
                    <Input
                      onChange={handleInputChange.bind(null, 'static_rollup_percent')}
                      style={{}}
                      type='number'
                      value={data.static_rollup_percent || ''}
                    />
                  </Field>
                )}
              </>
            )}
          </FieldGroup>
          {/* GMB Charge */}
          <FieldGroup contained={true} label='GMB Charge'>
            <Field label='Charge BPs' style={{}}>
              <Input
                onChange={handleInputChange.bind(null, 'charge_bps')}
                style={{}}
                type='number'
                value={data.charge_bps || ''}
              />
            </Field>{' '}
            <Field label='Charge Basis Type' style={{}}>
              <Select
                onChange={handleInputChange.bind(null, 'charge_basis_type')}
                options={[
                  { value: 'benefit_base', label: 'Benefit Base' },
                  { value: 'account_value', label: 'Account Value' },
                  { value: 'fixed_account_value', label: 'Fixed Account Value' },
                  { value: 'accumulation_value', label: 'Accumulation Value' }
                ]}
                style={{}}
                value={data.charge_basis_type}
              />
            </Field>{' '}
            <Field label='Charge Frequency' style={{}}>
              <Select
                onChange={handleInputChange.bind(null, 'charge_frequency')}
                options={[
                  { value: 'annual', label: 'Annual' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'monthly', label: 'Monthly' }
                ]}
                style={{}}
                value={data.charge_frequency}
              />
            </Field>
            <Field label='Charge Term' style={{}}>
              <Input
                onChange={handleInputChange.bind(null, 'charge_term')}
                style={{}}
                value={data.charge_term || ''}
              />
            </Field>
          </FieldGroup>
          {/* Legacy Compatibility Fields */}
          <Field style={{}}>
            <Checkbox
              checked={data.use_quote_column_states}
              label='Use Quote Rate States'
              onChange={handleCheckboxChange.bind(null, 'use_quote_column_states')}
            />
          </Field>
          {!data.use_quote_column_states && (
            <Field label='States' style={{}}>
              {' '}
              <Checklist
                columns={3}
                onChange={handleStatesChange}
                options={STATES}
                selected={STATES.filter(state => data[`state_${state.value}`]).map(s => s.value)}
                showSelectAll={true}
                style={{}}
              />
            </Field>
          )}
          <Field label='Modal Factor' style={{}}>
            <Select
              onChange={handleInputChange.bind(null, 'modal_factor_id')}
              options={[{ value: '', label: 'Use Quote Rate Modal Factor' }].concat(
                modal_factors.map(mf => ({ value: mf.id, label: mf.name }))
              )}
              style={{}}
              value={data.modal_factor_id}
            />
          </Field>{' '}
          <Field label='Bands' style={{}}>
            <Checklist
              columns={2}
              onChange={handleChecklistChange.bind(null, 'band_ids')}
              options={bands.map(b => ({ value: b.id, label: b.band_name }))}
              selected={data.band_ids}
              showSelectAll={true}
              style={{}}
            />
          </Field>
          <Field label='Products' style={{}}>
            <Checklist
              columns={3}
              onChange={handleChecklistChange.bind(null, 'product_ids')}
              options={products.map(p => ({ value: p.id, label: p.product_name }))}
              selected={data.product_ids}
              showSelectAll={true}
              style={{}}
            />
          </Field>
          <Field label='Health Categories' style={{}}>
            <Checklist
              columns={3}
              onChange={handleChecklistChange.bind(null, 'health_category_ids')}
              options={health_categories.map(hc => ({
                value: hc.id,
                label: hc.carrier_health_category
              }))}
              selected={data.health_category_ids}
              showSelectAll={true}
              style={{}}
            />
          </Field>
        </>
      )}
    </Drawer>
  );
};

Edit.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  rider: PropTypes.object,
  riderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool
};

export default Edit;
